import React, { useState, useEffect } from 'react'
import { Container, Row, Col, Card, Spinner, Alert, Breadcrumb } from 'react-bootstrap'
import { Link, useNavigate, useParams } from 'react-router-dom'
import http from '../../helpers/Request'
import { formatDate, stripHtml, truncateText } from '../../helpers/BlogText'
import { Helmet } from "react-helmet-async"

const BlogDetail = () => {
  const { slug } = useParams()
  const navigate = useNavigate()
  const [blog, setBlog] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [blogs, setBlogs] = useState([])
  const [errorBlogs, setErrorBlogs] = useState('')
  const [loadingBlogs, setLoadingBlogs] = useState(true)
  const [related, setRelated] = useState([])
  
  // Base URL for your site - replace with your actual domain
  const BASE_URL = 'https://siberwander.com';
  
  // Function to get absolute URL for images
  const getAbsoluteImageUrl = (imageUrl) => {
    if (!imageUrl) return `${BASE_URL}/default-social-image.jpg`;
    
    // If image URL is already absolute, return as is
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      return imageUrl;
    }
    
    // If image URL is relative, convert to absolute
    return `${BASE_URL}${imageUrl.startsWith('/') ? '' : '/'}${imageUrl}`;
  }
  
  const fetchBlogs = async () => {
    try {
      setLoadingBlogs(true)
      const response = await http.get(`/api/blog/latest?id=${blog.id}&page=${1}&limit=${4}`)
      
      setBlogs(response.data.data)
    } catch (err) {
      setErrorBlogs('Failed to load news')
      console.error('Error fetching news:', err)
    } finally {
      setLoadingBlogs(false)
    }
  }

  const fetchBlogRelated = async () => {
    try {
      setLoadingBlogs(true)
      const response = await http.get(`/api/blog/related?id=${blog.id}&category_slug=${blog.category_slug}&page=${1}&limit=${3}`)
      
      setRelated(response.data.data)
    }catch (err) {

    }
  }
  
  useEffect(() => {
    const fetchBlog = async () => {
      try {
        setLoading(true)
        const response = await http.get(`/api/blog/${slug}`)
        setBlog(response.data.data)
      } catch (err) {
        if (err.response?.status === 404) {
          setError('News not found')
        } else {
          setError('Failed to load news')
        }
        console.error('Error fetching news:', err)
      } finally {
        setLoading(false)
      }
    }
    if (slug) {
      fetchBlog()
    }
  }, [slug])
  
  useEffect(() => {
    if(blog){
      fetchBlogs()
      fetchBlogRelated()
    }
  }, [blog])
  
  if (loading) {
    return (
      <div className="py-5">
        <Container>
          <div className="text-center">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="mt-3">Loading news...</p>
          </div>
        </Container>
      </div>
    )
  }
  
  if (error) {
    return (
      <div className="py-5">
        <Container>
          <Alert variant="danger">
            {error}
            <div className="mt-3">
              <Link to="/news" className="btn btn-primary">
                Back to News
              </Link>
            </div>
          </Alert>
        </Container>
      </div>
    )
  }

  // Get absolute image URL
  const absoluteImageUrl = getAbsoluteImageUrl(blog.image);
  
  // Prepare meta description
  const metaDescription = truncateText(stripHtml(blog.content), 160);
  
  return (
    <div className="py-5">
      <Helmet>
        <title>{blog.title} | Siberwander</title>
        <meta name="description" content={metaDescription} />
        
        {/* Open Graph (Facebook, LinkedIn, etc.) */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={blog.title} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content={absoluteImageUrl} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={blog.title} />
        <meta property="og:url" content={`${BASE_URL}/news/${blog.slug}`} />
        <meta property="og:site_name" content="Siberwander" />
        <meta property="article:published_time" content={blog.publish_date} />
        
        {/* Twitter Cards */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={blog.title} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={absoluteImageUrl} />
        <meta name="twitter:image:alt" content={blog.title} />
        
        {/* Additional meta tags for better sharing */}
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`${BASE_URL}/news/${blog.slug}`} />
        
        {/* WhatsApp specific */}
        <meta property="og:locale" content="en_US" />
      </Helmet>
      {
        !blog ? (
          <div className="py-5">
            <Container>
              <Alert variant="warning">
                News not found
                <div className="mt-3">
                  <Link to="/news" className="btn btn-primary">
                    Back to News
                  </Link>
                </div>
              </Alert>
            </Container>
          </div>
        ):(
          <Container>
            <Row>
              <Col>
                <Breadcrumb className="mb-4 ps-2 pe-2">
                  <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/" }}>
                    Home
                  </Breadcrumb.Item>
                  <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/news" }}>
                    News
                  </Breadcrumb.Item>
                  <Breadcrumb.Item active>{blog.title}</Breadcrumb.Item>
                </Breadcrumb>
              </Col>
            </Row>
            <Row className="g-4">
              <Col lg={8} xl={9}>
                <Card className="border-0 shadow-lg mb-4 overflow-hidden">
                  {blog.image && (
                    <Card.Img
                      variant="top"
                      src={blog.image}
                      alt={blog.title}
                      className="img-fluid rounded-top"
                      onError={(e) => {
                        e.target.style.display = 'none'
                      }}
                    />
                  )}
                  <Card.Body className="p-4 pb-0">
                    <h1 className="h2 mb-3 fw-bold">{blog.title}</h1>
                    <div className="text-muted mb-4">
                      <strong className='text-brand-secondary pointer' onClick={() => navigate(`/news?category=${blog.category_slug}`)}>{blog.category}</strong> | <small>Published on {blog.publish_date ? formatDate(blog.publish_date) : ""}</small>
                    </div>
                    <div
                      className="blog-content mb-4 text-break"
                      dangerouslySetInnerHTML={{ __html: blog.content }}
                    />
                  </Card.Body>
                </Card>
              </Col>
              <Col lg={4} xl={3}>
              {
                blogs.length > 0 &&
                <div className="mb-4">
                  <h5 className="fw-bold text-brand mb-3">
                    <i className="bi bi-newspaper"></i>Recent News
                  </h5>
                  <Row className="g-3">
                  {blogs.map((item) => (
                    <Col xs={12} key={"blog-"+item.id}>
                      <Card className="h-100 shadow-sm border-0 hover-card">
                        {item.image && (
                          <Card.Img
                            variant="top"
                            src={item.image}
                            alt={item.title}
                            className="img-fluid rounded-top"
                            style={{ height: '130px', objectFit: 'cover' }}
                            onError={(e) => {
                              e.target.style.display = 'none'
                            }}
                          />
                        )}
                        <Card.Body className="d-flex flex-column pointer p-3" onClick={() => navigate(`/news/${item.slug}`)}>
                          <Card.Subtitle className="fw-bold mb-2">{item.title}</Card.Subtitle>
                          <div className="mt-auto">
                            <small className="d-block">
                              <strong className='text-brand-secondary'>{item.category}</strong>
                            </small>
                            <small className="d-block text-muted">
                              {item.publish_date ? formatDate(item.publish_date) : ""}
                            </small>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                  </Row>
                  <div className="mt-4 text-center">
                    <Link to="/news" className="btn btn-primary btn-sm px-4">
                      <i className="bi bi-arrow-right me-1"></i>See All News
                    </Link>
                  </div>
                </div>
              }
              </Col>
              <Col sm={12}>
                <h5 className="fw-bold text-brand mb-3">
                  <i className="bi bi-newspaper"></i>Related News
                </h5>
                <Row>
                  {related.map((item) => (
                    <Col md={4} key={"blog-"+item.id}>
                      <Card className="h-100 shadow-sm border-0 hover-card">
                        {item.image && (
                          <Card.Img
                            variant="top"
                            src={item.image}
                            alt={item.title}
                            className="img-fluid rounded-top"
                            style={{ height: '200px', objectFit: 'cover' }}
                            onError={(e) => {
                              e.target.style.display = 'none'
                            }}
                          />
                        )}
                        <Card.Body className="d-flex flex-column pointer p-3" onClick={() => navigate(`/news/${item.slug}`)}>
                          <Card.Subtitle className="fw-bold mb-2">{item.title}</Card.Subtitle>
                          <Card.Text className="text-muted flex-grow-1">
                            {truncateText(stripHtml(blog.content))}
                          </Card.Text>
                          <div className="mt-auto mt-2">
                            <small className="d-block">
                              <strong className='text-brand-secondary'>{blog.category}</strong>
                            </small>
                            <small className="d-block text-muted">
                              {item.publish_date ? formatDate(item.publish_date) : ""}
                            </small>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </Col>
            </Row>
          </Container>
        )
      }
    </div>
  )
}

export default BlogDetail

